export interface AnnotationMentionUser {
  userId: string;
  displayName: string;
}

export type AnnotationMentionContentPart =
  | {
      type: 'text';
      text: string;
    }
  | {
      type: 'mention';
      userId: string;
      displayNameSnapshot: string;
      displayName: string;
      text: string;
    };

const ANNOTATION_USER_MENTION_REGEX = /@\[([^\]]+)]\(user:([^)]+)\)/g;
const MENTION_TRAILING_BOUNDARY = String.raw`(?=$|\s|[.,!?;:，。！？；：、）)\]}])`;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function sanitizeMentionDisplayName(value: string): string {
  return value.replaceAll(']', '］').trim();
}

export function buildAnnotationUserMentionToken(user: AnnotationMentionUser): string {
  const displayName = sanitizeMentionDisplayName(user.displayName);
  return `@[${displayName}](user:${user.userId})`;
}

export function encodeAnnotationUserMentions(
  content: string,
  users: AnnotationMentionUser[]
): string {
  const seen = new Set<string>();
  const mentionUsers = users
    .filter((user) => user.userId && user.displayName.trim())
    .filter((user) => {
      const key = `${user.userId}:${user.displayName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => right.displayName.length - left.displayName.length);

  return mentionUsers.reduce((current, user) => {
    const visibleMention = `@${user.displayName}`;
    const regex = new RegExp(`${escapeRegExp(visibleMention)}${MENTION_TRAILING_BOUNDARY}`, 'gu');
    return current.replace(regex, buildAnnotationUserMentionToken(user));
  }, content);
}

export function extractAnnotationMentionUserIds(
  content: string,
  fallbackUsers: AnnotationMentionUser[] = []
): string[] {
  const userIds = new Set<string>();
  for (const match of content.matchAll(ANNOTATION_USER_MENTION_REGEX)) {
    if (match[2]) {
      userIds.add(match[2]);
    }
  }

  const fallbackContent = userIds.size > 0 ? '' : content;
  for (const user of fallbackUsers) {
    if (fallbackContent.includes(`@${user.displayName}`)) {
      userIds.add(user.userId);
    }
  }

  return [...userIds];
}

export function parseAnnotationMentionContent(
  content: string,
  usersById: ReadonlyMap<string, AnnotationMentionUser> = new Map()
): AnnotationMentionContentPart[] {
  const parts: AnnotationMentionContentPart[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(ANNOTATION_USER_MENTION_REGEX)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push({ type: 'text', text: content.slice(lastIndex, start) });
    }

    const displayNameSnapshot = match[1] ?? '';
    const userId = match[2] ?? '';
    const displayName = usersById.get(userId)?.displayName || displayNameSnapshot;
    parts.push({
      type: 'mention',
      userId,
      displayNameSnapshot,
      displayName,
      text: `@${displayName}`,
    });
    lastIndex = start + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', text: content.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', text: content }];
}
